import * as actions from './actions'
import { passwordResetActions } from 'state/passwordReset'
import { signupActions } from 'state/signup'
import { success, failure } from 'utilities/actions'

import type { User, Feature, Organisation, ClientSettings } from 'types'
import { INVALID_OTP_CODE } from 'qap/constants'

export type State = {
  expiry: string | null | undefined;
  sessionRefresh: {
    refreshAtMs: number | null;
    scheduledAtMs: number | null;
  };
  user: User | null | undefined;
  permissions: string[];
  errors: any[];
  features: Feature[];
  loginFailures: number;
  verificationFailures: number;
  canSwitchOrganisations: boolean;
  organisations: Organisation[];
  currentlyEnrolled: boolean;
  participantTypes: string[];
  submitting: boolean;
  clientSettings: ClientSettings;
  loadingSession: boolean;
  message?: string;
};

type Session = {
  expiry: string;
  user: User;
  permissions: string[];
  features: Feature[];
  organisations: Organisation[];
  participantTypes?: string[];
  canSwitchOrganisations: boolean;
  secureId?: string;
  currentlyEnrolled?: boolean;
};

type JwtAuth = {
  secureId?: string | null | undefined;
  redirect?: string | null | undefined;
  notification?: string | null | undefined;
};

type Action = {
  type: string;
  user?: User;
  errors: any[];
  meta?: {
    user?: User;
  };
  data?: Session;
  permissions?: string[];
  features?: Feature[];
  organisations?: Organisation[];
  canSwitchOrganisations?: boolean;
  currentlyEnrolled?: boolean;
  participantTypes?: string[];
  clientSettings: any;
  payload?: any;
  message?: string;
} & JwtAuth;

export const initialState = {
  expiry: null,
  sessionRefresh: { refreshAtMs: null, scheduledAtMs: null },
  user: null,
  errors: [],
  loginFailures: 0,
  verificationFailures: 0,
  permissions: [],
  features: [],
  verifyingUser: false,
  verifiedUser: false,
  canSwitchOrganisations: false,
  organisations: [],
  currentlyEnrolled: false,
  participantTypes: [],
  submitting: false,
  clientSettings: {},
  loadingSession: false,
} as const

const reducer = (state: State, action: Action) => {
// @ts-expect-error - TS2322 - Type '{ readonly expiry: null; readonly sessionRefresh: { readonly refreshAtMs: null; readonly scheduledAtMs: null; }; readonly user: null; readonly errors: readonly []; readonly loginFailures: 0; readonly verificationFailures: 0; ... 9 more ...; readonly loadingSession: false; }' is not assignable to type 'State'.
  if (state === undefined) { state = initialState }
  switch (action.type) {
    case actions.API_SESSION_FETCH:
      return { ...state, loadingSession: true }
    case success(actions.API_SESSION_FETCH):
    case success(actions.API_SESSION_REFRESH):
    case success(actions.API_SESSION_PASSWORD_AUTH_CREATE): {
      const {
// @ts-expect-error - TS2339 - Property 'expiry' does not exist on type 'Action'. - fall through
        expiry,
        user,
        permissions,
        features,
        canSwitchOrganisations,
        organisations,
        currentlyEnrolled,
        participantTypes,
        clientSettings,
      } = action
      // Preserve pre-auth queued `clientSettings`.
      // Due to a race between the auth response and the flush request,
      // we keep them temporarily until the server determines what to keep or drop
      const localClientSettings = state?.clientSettings || {}

      return {
        ...initialState,
        loadingSession: false,
        message: undefined,
        expiry,
        user,
        permissions,
        features,
        canSwitchOrganisations,
        organisations,
        currentlyEnrolled: currentlyEnrolled || false,
        participantTypes: participantTypes || [],
        clientSettings: {
          ...clientSettings,
          ...localClientSettings
        },
      }
    }
    case success(actions.API_SESSION_JWT_AUTH_CREATE): {
      const { data: { secureId } = {}, redirect, notification } = action

      return {...state,
        jwt: {
          secureId,
          redirect,
          notification
        }
      }
    }
    case failure(actions.API_SESSION_PASSWORD_AUTH_CREATE): {
      const { errors } = action
      if (errors.some(err => err.code === INVALID_OTP_CODE)) {
        return {
          ...state,
          errors,
          verificationFailures: state.verificationFailures + 1
        }
      } else {
        return {
          ...state,
          errors,
          loginFailures: state.loginFailures + 1
        }
      }
    }
    case actions.API_SESSION_CLIENT_SETTINGS_UPDATE:
      return { ...state, submitting: true }
    case success(actions.API_SESSION_CLIENT_SETTINGS_UPDATE): {
      const { data = {} } = action

      return {
        ...state,
        submitting: false,
        clientSettings: data,
      }
    }
    case actions.SET_LOCAL_CLIENT_SETTINGS: {
      const { key, value } = action.payload ?? {}
      return {
        ...state,
        clientSettings: {
          ...state.clientSettings,
          [key]: value,
        },
      }
    }
    case failure(actions.LOGOUT_USER):
    case failure(actions.API_SESSION_CLIENT_SETTINGS_UPDATE):
    case failure(actions.API_SESSION_FETCH):
    case failure(actions.API_SESSION_JWT_AUTH_CREATE): {
      const { errors } = action
      return { ...state, errors, submitting: false, loadingSession: false }
    }
    case success(signupActions.API_SIGNUPS_CREATE):
    case success(passwordResetActions.API_PASSWORD_RESETS_RESETS_CREATE): {
      const { data = {} } = action
// @ts-expect-error - TS2339 - Property 'expiry' does not exist on type '{}'. | TS2339 - Property 'user' does not exist on type '{}'. | TS2339 - Property 'permissions' does not exist on type '{}'. | TS2339 - Property 'features' does not exist on type '{}'. - fall through
      const { expiry, user, permissions, features } = data
      return {
        ...state,
        expiry,
        user,
        permissions,
        features,
        submitting: false,
        message: undefined,
      }
    }
    case actions.AUTH_EXPIRE_SESSION:
      return { ...initialState, message: action.message }
    case actions.INITIALIZE_LOGIN: {
      return { ...initialState }
    }
    case actions.AUTH_SESSION_REFRESH_SCHEDULED: {
      const { refreshAtMs, scheduledAtMs } = action.payload
      return {
        ...state,
        sessionRefresh: { refreshAtMs, scheduledAtMs },
      }
    }
    default:
      return state
  }
}

export default reducer
